import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Description as CsvIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useEntitlementUpload } from '../hooks/useEntitlementUpload';
import {
  downloadEntitlementUploadTemplate,
  isSupportedEntitlementUploadFile,
} from '../utils/entitlementUploadFile';
import { useAuth } from '../contexts/AuthContext';

const activeStatuses = ['validating', 'uploading', 'awaiting_upload', 'queued', 'processing'];

const EntitlementUpload: React.FC = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('upload', 'editar');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [templateError, setTemplateError] = useState<string>();
  const { state, start, downloadReport, clear } = useEntitlementUpload();

  const isBusy = activeStatuses.includes(state.status);
  const isTerminal = ['completed', 'failed', 'expired'].includes(state.status);
  const processingProgress = state.totalRows && state.totalRows > 0
    ? Math.min(100, Math.round(state.processedRows / state.totalRows * 100))
    : 0;
  const completedRows = state.deliveredRows + state.pendingRows;

  const chooseFile = (file?: File) => {
    if (!file || isBusy) return;

    if (!isSupportedEntitlementUploadFile(file)) {
      setTemplateError('Selecione um arquivo CSV ou XLSX.');
      return;
    }

    setTemplateError(undefined);
    setSelectedFile(file);
    setShowConfirmation(true);
  };

  const confirmUpload = () => {
    if (!selectedFile) return;
    setShowConfirmation(false);
    void start(selectedFile);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const cancelUpload = () => {
    setShowConfirmation(false);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDownloadTemplate = async () => {
    try {
      setTemplateError(undefined);
      await downloadEntitlementUploadTemplate();
    } catch {
      setTemplateError('Não foi possível gerar o template.');
    }
  };

  return (
    <>
      <Paper sx={{ p: 3, m: 2 }}>
        <Typography variant="h6" gutterBottom>
          Upload de Concessões
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Envie um CSV ou XLSX de até 2 MB e 5.000 linhas. O processamento é feito no servidor e gera um relatório ao final.
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => void handleDownloadTemplate()} disabled={isBusy}>
            Baixar template XLSX
          </Button>
        </Box>

        <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Upload.'}>
          <Box>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: isDragging ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                backgroundColor: isDragging ? 'action.hover' : 'background.paper',
                cursor: isBusy || !canManage ? 'not-allowed' : 'pointer',
                opacity: isBusy || !canManage ? 0.5 : 1,
                pointerEvents: isBusy || !canManage ? 'none' : 'auto',
              }}
              onClick={() => {
                if (isBusy || !canManage) return;
                fileInputRef.current?.click();
              }}
              onDrop={(event) => {
                event.preventDefault();
                setIsDragging(false);
                if (!canManage) return;
                chooseFile(event.dataTransfer.files[0]);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
            >
              <CsvIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Arraste um arquivo XLSX ou CSV aqui ou clique para selecionar
              </Typography>
              <Typography variant="body2" color="text.secondary">
                O arquivo deve conter as colunas CNPJ, CPF, Combo e Quantidade. Quantidade vazia vale 1.
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                aria-label="Selecionar arquivo de concessões"
                accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                hidden
                disabled={isBusy || !canManage}
                onChange={(event) => chooseFile(event.target.files?.[0])}
              />
            </Box>
          </Box>
        </Tooltip>

        {templateError && <Alert severity="error" sx={{ mt: 2 }}>{templateError}</Alert>}
        {state.failure && <Alert severity="error" sx={{ mt: 2 }}>{state.failure}</Alert>}

        {state.status !== 'idle' && (
          <Box sx={{ mt: 3 }}>
            {state.status === 'validating' && <Typography>Validando arquivo...</Typography>}
            {state.status === 'uploading' && (
              <>
                <Typography>Enviando arquivo original...</Typography>
                <LinearProgress variant="determinate" value={state.uploadProgress} sx={{ mt: 1 }} />
                <Typography variant="caption">{state.uploadProgress}% enviado</Typography>
              </>
            )}
            {['awaiting_upload', 'queued', 'processing'].includes(state.status) && (
              <>
                <Typography>{state.status === 'queued' ? 'Aguardando processamento...' : 'Processando no servidor...'}</Typography>
                {state.totalRows ? (
                  <>
                    <LinearProgress variant="determinate" value={processingProgress} sx={{ mt: 1 }} />
                    <Typography variant="caption">
                      {state.processedRows}/{state.totalRows} linhas processadas ({processingProgress}%)
                    </Typography>
                  </>
                ) : <LinearProgress sx={{ mt: 1 }} />}
              </>
            )}
            {state.status === 'completed' && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon color="success" fontSize="small" />
                  <Typography color="success.main">Processamento concluído.</Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Livros entregues: {state.createdEntitlements}. Concessões pendentes criadas: {state.createdPendingEntitlements}.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Linhas processadas: {state.processedRows}/{state.totalRows}. Concluídas: {completedRows}. Com erro: {state.errorRows}.
                </Typography>
                <Button sx={{ mt: 1 }} variant="contained" startIcon={<DownloadIcon />} onClick={() => void downloadReport()} disabled={!state.report}>
                  Baixar relatório
                </Button>
              </Box>
            )}
            {state.status === 'expired' && <Alert severity="warning">O relatório deste upload expirou.</Alert>}
            {isTerminal && (
              <Box sx={{ mt: 2 }}>
                <Button onClick={clear}>Enviar outro arquivo</Button>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      <Dialog open={showConfirmation} onClose={cancelUpload} maxWidth="sm" fullWidth>
        <DialogTitle>Iniciar processamento?</DialogTitle>
        <DialogContent>
          <Typography>
            O arquivo original será enviado ao armazenamento privado e processado no servidor. O relatório ficará disponível ao término nesta sessão.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelUpload}>Cancelar</Button>
          <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Upload.'}>
            <span>
              <Button variant="contained" onClick={confirmUpload} disabled={!canManage}>
                Enviar e processar
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EntitlementUpload;
